import React, { useState, useEffect } from "react";
import Icon from "../../components/AppIcon";
import { arrivalNotificationAPI } from "../../utils/api";
import { useNotifier } from '../../context/NotificationContext';

const REMARKS_OPTIONS = [
  { value: "", label: "-- Select --" },
  { value: "PTM", label: "PTM" },
  { value: "NTL", label: "NTL" },
  { value: "POSTPONED", label: "POSTPONED" },
  { value: "CANCEL_ENTRY", label: "CANCEL ENTRY" },
];

export default function UpdateRemarksModal({
  isOpen,
  onClose,
  notification,
  onSuccess,
}) {
  const notify = useNotifier();
  const [workerRemarks, setWorkerRemarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Initialize worker remarks from notification data
  useEffect(() => {
    if (isOpen && notification?.workers) {
      const initialRemarks = {};
      notification.workers.forEach((worker) => {
        initialRemarks[worker.applicationWorkerId] = worker.remarks || "";
      });
      setWorkerRemarks(initialRemarks);
      setSearchTerm("");
    }
  }, [isOpen, notification]);

  if (!isOpen || !notification) return null;

  const workers = notification.workers || [];

  // Filter workers by search term
  const filteredWorkers = workers.filter((worker) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      worker.fullName?.toLowerCase().includes(search) ||
      worker.passportNo?.toLowerCase().includes(search) ||
      String(worker.workerId).includes(search)
    );
  });

  // Handle individual worker remarks change
  const handleRemarksChange = (applicationWorkerId, value) => {
    setWorkerRemarks((prev) => ({
      ...prev,
      [applicationWorkerId]: value,
    }));
  };

  // Apply same remarks to all workers
  const handleApplyToAll = (value) => {
    const updated = {};
    workers.forEach((worker) => {
      updated[worker.applicationWorkerId] = value;
    });
    setWorkerRemarks(updated);
  };

  // Check if there are any changes
  const getChangedWorkers = () => {
    return workers.filter((worker) => {
      const currentValue = workerRemarks[worker.applicationWorkerId] || "";
      const originalValue = worker.remarks || "";
      return currentValue !== originalValue;
    });
  };

  const changedWorkers = getChangedWorkers();
  const hasChanges = changedWorkers.length > 0;

  // Save all changes
  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      const payload = changedWorkers.map((worker) => ({
        applicationWorkerId: worker.applicationWorkerId,
        remarks: workerRemarks[worker.applicationWorkerId] || null,
      }));

      await arrivalNotificationAPI.updateWorkerRemarks(payload);
      onSuccess(`Updated remarks for ${changedWorkers.length} worker(s)`);
    } catch (error) {
      console.error("Failed to save remarks:", error);
      notify.error(error.response?.data?.error || "Failed to save remarks");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-4xl overflow-hidden text-left align-middle transition-all transform bg-white rounded-xl shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#2D9B9B] to-[#1A7A7A] px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Icon name="Edit3" size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Update Worker Remarks
                  </h3>
                  <p className="text-teal-100 text-sm">
                    {notification.companyName} - {notification.workerCount} worker(s)
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-teal-200 transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border-b border-blue-100 px-6 py-3">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={18} className="text-blue-500 mt-0.5" />
              <div className="text-sm text-blue-700">
                <strong>Post-Arrival Remarks:</strong> Update remarks for workers after their arrival.
                Changes will be saved when you click "Save Changes".
              </div>
            </div>
          </div>

          {/* Bulk Action */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">Apply to all workers:</span>
                <select
                  onChange={(e) => handleApplyToAll(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B]"
                >
                  {REMARKS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Search" size={16} className="text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or passport..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B]"
                />
              </div>
            </div>
          </div>

          {/* Workers Table */}
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Worker ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Passport
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Entry Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Current Remarks
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    New Remarks
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredWorkers.map((worker, index) => {
                  const currentValue = workerRemarks[worker.applicationWorkerId] || "";
                  const originalValue = worker.remarks || "";
                  const isChanged = currentValue !== originalValue;

                  return (
                    <tr
                      key={worker.applicationWorkerId}
                      className={isChanged ? "bg-teal-50" : "hover:bg-gray-50"}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {worker.workerId}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {worker.fullName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {worker.passportNo}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {worker.entryDate || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {originalValue ? (
                          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                            {originalValue}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={currentValue}
                          onChange={(e) =>
                            handleRemarksChange(worker.applicationWorkerId, e.target.value)
                          }
                          className={`w-full px-2 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#2D9B9B] focus:border-[#2D9B9B] ${
                            isChanged
                              ? "border-[#2D9B9B] bg-teal-50"
                              : "border-gray-300"
                          }`}
                        >
                          {REMARKS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {hasChanges ? (
                <span className="text-[#2D9B9B] font-medium">
                  <Icon name="AlertCircle" size={16} className="inline mr-1" />
                  {changedWorkers.length} worker(s) with changes
                </span>
              ) : (
                <span className="text-gray-500">No changes made</span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="flex items-center space-x-2 px-4 py-2 bg-[#2D9B9B] text-white rounded-lg hover:bg-[#1A7A7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <Icon name="Loader" size={18} className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={18} />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
